import React from "react";
import { Navigate } from "react-router-dom";
import { RegisterAcademy } from "./pages/RegisterAcademy";
import { Login } from "./pages/Login";
import { RegisterStudent } from "./pages/RegisterStudent";
import { ActivateAccount } from "./pages/ActivateAccount";
import { ForgotPassword } from "./pages/ForgotPassword";
import { ResetPassword } from "./pages/ResetPassword";
import { Dashboard } from "./pages/Dashboard";
import { RequireRole } from "../../app/router/RequireRole";

/**
 * Auth feature routes — TASK-07 (R6: owned exclusively by src/features/auth/).
 *
 * ── Public (no auth required) ──────────────────────────────────────────────
 *   /registro-academia              Owner onboarding (no slug — creates the tenant)
 *   /:slug/login                    Login for any role in the given academy
 *   /:slug/registro                 Student self-registration
 *   /:slug/activar/:uid/:token      Email activation (Djoser)
 *   /:slug/recuperar                Password-reset request (Djoser)
 *   /:slug/recuperar/:uid/:token    Password-reset confirmation (Djoser)
 *
 * ── Protected (RequireRole — any authenticated user) ───────────────────────
 *   /:slug/app/dashboard            Role dispatcher → OwnerDashboard |
 *                                   TeacherDashboard | StudentDashboard
 *
 * ── Extension points for downstream features ───────────────────────────────
 *   TASK-09  → register routes under /:slug/app/academia/...   (RequireRole roles=['OWNER'])
 *   TASK-12  → register routes under /:slug/app/agenda/...     (RequireRole roles=['TEACHER','OWNER'])
 *   TASK-14  → register routes under /:slug/app/mi-espacio/... (RequireRole roles=['STUDENT'])
 *   TASK-16  → register routes under /:slug/app/honorarios/... (RequireRole roles=['OWNER'])
 *   TASK-18  → register routes under /:slug/app/admin/...      (RequireRole roles=['OWNER'])
 *
 * All downstream features register their own routes.js in AppRouter.js; nobody
 * edits this file to add non-auth routes (R6).
 */
const authRoutes = [
  // ── Public ────────────────────────────────────────────────────────────────
  { path: "/registro-academia", element: <RegisterAcademy />, public: true },
  // Login sin academia. Es donde redirigen los guards cuando no hay slug
  // (RequireRole, RequireSuperAdmin) y donde aterriza el staff de plataforma al
  // cerrar sesión, porque no pertenece a ninguna academia.
  //
  // Eclipsa a propósito el `/login` de legacyRoutes (src/pages/login.js), que se
  // registra después: aquel manda el campo `username` en vez de `email` —así que
  // entrar con el correo daba 401—, no muestra el error (solo console.error), lee
  // un `response.data.refresh` que el backend ya no devuelve (va en cookie
  // HttpOnly) y no pasa por AuthProvider. Es un paso más de la migración
  // legacy→features; cuando se borre src/pages/login.js esta nota sobra.
  { path: "/login", element: <Login />, public: true },
  { path: "/:slug/login", element: <Login />, public: true },
  { path: "/:slug/registro", element: <RegisterStudent />, public: true },
  {
    path: "/:slug/activar/:uid/:token",
    element: <ActivateAccount />,
    public: true,
  },
  { path: "/:slug/recuperar", element: <ForgotPassword />, public: true },
  {
    path: "/:slug/recuperar/:uid/:token",
    element: <ResetPassword />,
    public: true,
  },
  // Enlace del correo de restablecimiento de Djoser. Sin slug: el token
  // identifica al usuario, no la academia, y quien lo pide (staff de plataforma,
  // o el endpoint recibe solo el email) puede no tener academia asociada. Debe
  // coincidir con settings.DJOSER['PASSWORD_RESET_CONFIRM_URL'] del backend.
  {
    path: "/password/reset/confirm/:uid/:token",
    element: <ResetPassword />,
    public: true,
  },

  // ── Protected ─────────────────────────────────────────────────────────────
  {
    path: "/:slug/app",
    element: <RequireRole />,
    children: [
      // Landing on /:slug/app with no sub-path → dashboard.
      { index: true, element: <Navigate to="dashboard" replace /> },
      { path: "dashboard", element: <Dashboard /> },
    ],
  },
  // Bare tenant root (/:slug or /:slug/) → dashboard when authenticated, else
  // RequireRole sends to /:slug/login. Prevents landing on an unmatched route
  // that renders an empty shell.
  {
    path: "/:slug",
    element: <RequireRole />,
    children: [{ index: true, element: <Navigate to="app/dashboard" replace /> }],
  },
];

export default authRoutes;
